<template>
  <div class="w-full h-full relative">
    <!-- Chart Container -->
    <div ref="sunburstContainer" class="w-full h-full"></div>
    
    <!-- Center Info Display -->
    <div 
      v-if="centerInfo"
      class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 pointer-events-none text-center"
      :style="{ width: centerCircleRadius * 2 + 'px' }"
    >
      <div class="text-xs text-gray-500 mb-1">{{ centerInfo.label }}</div>
      <div class="text-2xl font-bold text-gray-900">{{ formatValue(centerInfo.value) }}</div>
      <div class="text-xs text-gray-600 mt-1">{{ centerInfo.subtitle }}</div>
    </div>

    <!-- Breadcrumb Navigation -->
    <div
      v-if="breadcrumbs.length > 0"
      class="absolute top-4 left-4 flex items-center gap-2 bg-white/90 backdrop-blur-sm px-3 py-2 rounded-lg shadow-sm"
    >
      <button
        @click="resetZoom"
        class="text-xs text-primary-600 hover:text-primary-800 font-medium flex items-center gap-1"
      >
        <font-awesome-icon :icon="['fas', 'home']" class="w-3 h-3" />
        Root
      </button>
      <template v-for="(crumb, index) in breadcrumbs" :key="index">
        <font-awesome-icon :icon="['fas', 'chevron-right']" class="w-2 h-2 text-gray-400" />
        <button
          @click="zoomToBreadcrumb(index)"
          class="text-xs text-gray-700 hover:text-primary-600 font-medium max-w-[150px] truncate"
          :title="crumb.name"
        >
          {{ crumb.name }}
        </button>
      </template>
    </div>

    <!-- Tooltip -->
    <div
      v-if="tooltip.visible"
      class="fixed pointer-events-none bg-gray-900 text-white px-3 py-2 rounded-lg shadow-lg text-sm z-50"
      :style="{ left: tooltip.x + 'px', top: tooltip.y + 'px', transform: 'translate(10px, -50%)' }"
    >
      <div class="font-semibold mb-1">{{ tooltip.name }}</div>
      <div class="text-gray-300">
        {{ formatValue(tooltip.value) }} {{ getValueLabel(tooltip.value) }}
      </div>
      <div class="text-gray-400 text-xs mt-1">
        {{ tooltip.percentage }}% of {{ tooltip.parentName || 'total' }}
      </div>
      <div class="text-primary-300 text-xs mt-1">Click to zoom in</div>
    </div>
  </div>
</template>

<script>
import * as d3 from 'd3';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faHome, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { ColorManager } from '@/utils/ColorManager.js';
import { isDemoMode, spoofText } from '@/utils/demoSpoof';

library.add(faHome, faChevronRight);

export default {
  name: 'SunburstChart',
  components: {
    FontAwesomeIcon
  },
  emits: ['segment-selected'],
  props: {
    chartData: {
      type: Array,
      required: true
    },
    leafNameKey: {
      type: String,
      required: true
    },
    selectedTagTypeIds: {
      type: Array,
      default: () => []
    },
    sizingMode: {
      type: String,
      default: 'count',
      validator: (value) => ['count', 'attribution'].includes(value)
    },
    width: {
      type: Number,
      default: null
    },
    height: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      svg: null,
      root: null,
      currentNode: null,
      radius: 0,
      centerCircleRadius: 0,
      breadcrumbs: [],
      centerInfo: null,
      tooltip: {
        visible: false,
        x: 0,
        y: 0,
        name: '',
        value: 0,
        percentage: 0,
        parentName: ''
      },
      resizeObserver: null,
      colorManager: null
    };
  },
  watch: {
    chartData: {
      handler() {
        this.updateChart();
      },
      deep: true
    },
    selectedTagTypeIds: {
      handler() {
        this.updateChart();
      },
      deep: true
    },
    sizingMode() {
      this.updateChart();
    }
  },
  mounted() {
    this.initColorManager();
    this.updateChart();

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        this.updateChart();
      });
      this.resizeObserver.observe(this.$refs.sunburstContainer);
    } else {
      window.addEventListener('resize', this.updateChart);
    }
  },
  beforeUnmount() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    } else {
      window.removeEventListener('resize', this.updateChart);
    }
  },
  methods: {
    initColorManager() {
      this.colorManager = new ColorManager();
    },

    getTagTypeName(tagTypeId) {
      for (const entity of this.chartData) {
        if (entity.infotagtypes) {
          const tagType = entity.infotagtypes.find(tt => tt.tagtype_id === tagTypeId);
          if (tagType) return tagType.tagtype_name;
        }
      }
      return `TagType ${tagTypeId}`;
    },

    entityHasTagsForType(entity, tagTypeId) {
      const entityTagType = entity.infotagtypes?.find(tt => tt.tagtype_id === tagTypeId);
      return entityTagType && entityTagType.tags && entityTagType.tags.length > 0;
    },

    getEntityValue(entity) {
      if (this.sizingMode === 'attribution') {
        return entity.total_attribution || 0;
      }
      return 1;
    },

    formatValue(value) {
      if (this.sizingMode === 'attribution') {
        return value.toLocaleString();
      }
      return value;
    },

    getValueLabel(value) {
      if (this.sizingMode === 'attribution') {
        return value === 1 ? 'patient' : 'patients';
      }
      return value === 1 ? 'entity' : 'entities';
    },

    buildHierarchicalData() {
      if (this.selectedTagTypeIds.length === 0) return null;

      this.colorManager.reset();

      const hierarchicalFlat = [
        {
          path: 'Root',
          parentPath: null,
          name: 'All Entities',
          value: 0,
          depth: 0,
          firstLevelAncestorPath: null,
          firstLevelIndex: -1
        }
      ];

      const aggregationMap = new Map();
      aggregationMap.set('Root', 0);

      const firstLevelNodes = new Map();
      let firstLevelCounter = 0;

      // Tag values are entity/provider/practice names rendered as node labels,
      // breadcrumbs and tooltips (Vue template + emits) the DOM spoof engine can't
      // reliably reach. Spoof them deterministically so aggregation paths stay
      // consistent (same real name -> same fake -> same path).
      const demo = isDemoMode();

      this.chartData.forEach(entity => {
        const entityValue = this.getEntityValue(entity);

        if (this.sizingMode === 'attribution' && entityValue === 0) {
          return;
        }

        let parentPath = 'Root';
        let fullPath = 'Root';
        let firstLevelAncestorPath = null;
        let firstLevelIndex = -1;

        this.selectedTagTypeIds.forEach((tagTypeId, depth) => {
          const entityTagType = entity.infotagtypes?.find(tt => tt.tagtype_id === tagTypeId);
          const tags = entityTagType?.tags || [];
          const tagTypeName = this.getTagTypeName(tagTypeId);

          let nodeLabel, tagValue;
          if (tags.length === 0) {
            nodeLabel = `${tagTypeName}: Untagged`;
            tagValue = 'Untagged';
          } else {
            tagValue = tags
              .map(t => (demo ? spoofText(t.name) : t.name))
              .join(', ');
            nodeLabel = `${tagTypeName}: ${tagValue}`;
          }

          fullPath = `${parentPath}/${nodeLabel}`;

          if (!aggregationMap.has(fullPath)) {
            if (depth === 0) {
              if (!firstLevelNodes.has(fullPath)) {
                firstLevelNodes.set(fullPath, firstLevelCounter);
                firstLevelCounter++;
              }
              firstLevelAncestorPath = fullPath;
              firstLevelIndex = firstLevelNodes.get(fullPath);
            }

            hierarchicalFlat.push({
              path: fullPath,
              parentPath: parentPath,
              name: nodeLabel,
              value: 0,
              depth: depth + 1,
              tagTypeId: tagTypeId,
              tagValue: tagValue,
              isUntagged: tagValue === 'Untagged',
              firstLevelAncestorPath: firstLevelAncestorPath ||
                hierarchicalFlat[aggregationMap.get(parentPath)]?.firstLevelAncestorPath,
              firstLevelIndex: firstLevelIndex >= 0 ? firstLevelIndex :
                hierarchicalFlat[aggregationMap.get(parentPath)]?.firstLevelIndex
            });
            aggregationMap.set(fullPath, hierarchicalFlat.length - 1);
          }

          if (depth === 0 && !firstLevelAncestorPath) {
            firstLevelAncestorPath = fullPath;
            firstLevelIndex = firstLevelNodes.get(fullPath);
          }

          parentPath = fullPath;
        });

        const nodeIndex = aggregationMap.get(fullPath);
        if (nodeIndex !== undefined) {
          hierarchicalFlat[nodeIndex].value += entityValue;
        }
      });

      return hierarchicalFlat;
    },

    updateChart() {
      const container = this.$refs.sunburstContainer;
      if (!container) return;

      const width = this.width || container.clientWidth;
      const height = this.height || container.clientHeight;

      if (width === 0 || height === 0) return;

      d3.select(container).selectAll('*').remove();

      if (this.selectedTagTypeIds.length === 0) {
        this.showEmptyState(container);
        return;
      }

      const hierarchicalFlat = this.buildHierarchicalData();
      if (!hierarchicalFlat) return;

      const stratify = d3.stratify()
        .id(d => d.path)
        .parentId(d => d.parentPath);

      let root;
      try {
        root = stratify(hierarchicalFlat);
        root.sum(d => d.value);
      } catch (error) {
        console.error('Error creating hierarchy:', error);
        return;
      }

      this.root = root;
      this.currentNode = root;

      this.radius = Math.min(width, height) / 2;
      this.centerCircleRadius = this.radius * 0.25;

      this.svg = d3.select(container)
        .append('svg')
        .attr('width', width)
        .attr('height', height)
        .append('g')
        .attr('transform', `translate(${width / 2},${height / 2})`);

      const partition = d3.partition()
        .size([2 * Math.PI, this.radius]);

      partition(root);

      const arc = d3.arc()
        .startAngle(d => d.x0)
        .endAngle(d => d.x1)
        .innerRadius(d => d.y0)
        .outerRadius(d => d.y1);

      const paths = this.svg.selectAll('path')
        .data(root.descendants().filter(d => d.depth > 0))
        .enter()
        .append('path')
        .attr('d', arc)
        .attr('fill', d => {
          const color = d.data.isUntagged
            ? '#e5e7eb'
            : this.colorManager.getColorForNode(
                d,
                d.data.firstLevelAncestorPath,
                d.data.firstLevelIndex
              );
          d._color = color;
          return color;
        })
        .attr('fill-opacity', 0.9)
        .attr('stroke', '#fff')
        .attr('stroke-width', 1.5)
        .style('cursor', 'pointer');

      paths
        .on('click', (event, d) => this.handleClick(event, d))
        .on('mouseenter', (event, d) => {
          d3.select(event.currentTarget)
            .transition()
            .duration(200)
            .attr('fill-opacity', 1);
          this.showTooltip(event, d);
        })
        .on('mousemove', (event) => this.moveTooltip(event))
        .on('mouseleave', (event, d) => {
          d3.select(event.currentTarget)
            .transition()
            .duration(200)
            .attr('fill-opacity', 0.9);
          this.hideTooltip();
        });

      this.updateCenterInfo(root);
    },

    handleClick(event, d) {
      event.stopPropagation();
      this.zoomTo(d);

      // Emit the segment data to parent
      this.$emit('segment-clicked', {
        name: d.data.name,
        path: d.data.path,
        depth: d.depth,
        value: d.value,
        tagTypeId: d.data.tagTypeId,
        tagValue: d.data.tagValue,
        isUntagged: d.data.isUntagged,
        node: d
      });
    },

    zoomTo(targetNode) {
      this.currentNode = targetNode;

      this.breadcrumbs = [];
      let current = targetNode;
      while (current.parent) {
        this.breadcrumbs.unshift({
          name: current.data.name,
          node: current
        });
        current = current.parent;
      }

      this.updateCenterInfo(targetNode);

      const x = d3.scaleLinear()
        .domain([targetNode.x0, targetNode.x1])
        .range([0, 2 * Math.PI]);

      const y = d3.scaleLinear()
        .domain([targetNode.y0, this.radius])
        .range([0, this.radius]);

      const arc = d3.arc()
        .startAngle(d => Math.max(0, Math.min(2 * Math.PI, x(d.x0))))
        .endAngle(d => Math.max(0, Math.min(2 * Math.PI, x(d.x1))))
        .innerRadius(d => Math.max(0, y(d.y0)))
        .outerRadius(d => Math.max(0, y(d.y1)));

      this.svg.selectAll('path')
        .transition()
        .duration(750)
        .attrTween('d', d => () => arc(d))
        .attr('fill', d => d._color)
        .attr('fill-opacity', d => {
          let ancestor = targetNode;
          while (ancestor) {
            if (d === ancestor.parent) return 0;
            ancestor = ancestor.parent;
          }
          return 0.9;
        })
        .attr('pointer-events', d => {
          let ancestor = targetNode;
          while (ancestor) {
            if (d === ancestor.parent) return 'none';
            ancestor = ancestor.parent;
          }
          return 'auto';
        });
    },

    resetZoom() {
      this.zoomTo(this.root);

      // Emit null to show all entities
      this.$emit('segment-clicked', null);
    },

    zoomToBreadcrumb(index) {
      const breadcrumb = this.breadcrumbs[index];
      if (breadcrumb) {
        this.zoomTo(breadcrumb.node);
      }
    },

    updateCenterInfo(node) {
      const total = this.root.value;
      const value = node.value;
      const percentage = ((value / total) * 100).toFixed(1);

      this.centerInfo = {
        label: node.depth === 0 ? (this.sizingMode === 'attribution' ? 'Total Attribution' : 'Total Entities') : node.data.name,
        value: value,
        subtitle: node.depth === 0
          ? `${this.selectedTagTypeIds.length} classification${this.selectedTagTypeIds.length === 1 ? '' : 's'}`
          : `${percentage}% of total`
      };
    },

    showTooltip(event, d) {
      const total = this.currentNode.value;
      const percentage = ((d.value / total) * 100).toFixed(1);

      this.tooltip = {
        visible: true,
        x: event.clientX,
        y: event.clientY,
        name: d.data.name,
        value: d.value,
        percentage: percentage,
        parentName: d.parent?.data.name
      };
    },

    moveTooltip(event) {
      this.tooltip.x = event.clientX;
      this.tooltip.y = event.clientY;
    },

    hideTooltip() {
      this.tooltip.visible = false;
    },

    showEmptyState(container) {
      const width = container.clientWidth;
      const height = container.clientHeight;

      const svg = d3.select(container)
        .append('svg')
        .attr('width', width)
        .attr('height', height);

      const g = svg.append('g')
        .attr('transform', `translate(${width / 2}, ${height / 2})`);

      g.append('text')
        .attr('text-anchor', 'middle')
        .attr('y', -20)
        .attr('class', 'text-lg font-medium fill-gray-500')
        .text('Select classification types to visualize');

      g.append('text')
        .attr('text-anchor', 'middle')
        .attr('y', 10)
        .attr('class', 'text-sm fill-gray-400')
        .text('Choose one or more tag types from the controls above');
    }
  }
};
</script>

<style scoped>
path {
  transition: fill-opacity 0.2s ease;
}
</style>
